import { ReactNode, useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Modal } from './Modal';

/** Inline typed-confirm input — rendered inside ConfirmDialog when requireTypedConfirm is set. */
export function TypedConfirmInput({
  value,
  name,
  placeholder,
  onChange,
}: {
  value: string;
  name: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mt-3 mb-1">
      <p className="ggj-text-meta vscode-text-secondary mb-1.5">
        Type <strong className="font-mono vscode-text-primary">{name}</strong> to confirm
      </p>
      <input
        type="text"
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-[10px] border vscode-border px-3 py-1.5 ggj-text-body bg-transparent vscode-text-primary focus:outline-none focus:ring-2 focus:ring-red-500/40"
        placeholder={placeholder ?? name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
  icon?: ReactNode;
  subtitle?: string;
  /**
   * When set, the confirm button stays disabled until the user types this exact string.
   * Pass the record name for named records, or "DELETE" for unnamed / bulk actions.
   */
  requireTypedConfirm?: string;
}

export function ConfirmDialog({
  open, onClose, onConfirm, title, message,
  confirmLabel = 'Confirm', variant = 'danger', loading = false,
  icon,
  subtitle,
  requireTypedConfirm,
}: ConfirmDialogProps) {
  const [typedValue, setTypedValue] = useState('');

  useEffect(() => {
    if (open) setTypedValue('');
  }, [open]);

  const defaultIcon = variant === 'danger'
    ? <AlertCircle className="w-4 h-4" strokeWidth={2} />
    : <CheckCircle2 className="w-4 h-4" strokeWidth={2} />;

  const typedMatch = requireTypedConfirm
    ? typedValue === requireTypedConfirm
    : true;

  const isDisabled = loading || !typedMatch;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={icon ?? defaultIcon}
      size="sm"
      variant={variant === 'danger' ? 'danger' : 'default'}
    >
      <p className="mb-4 ggj-text-body text-slate-700 dark:text-slate-300">{message}</p>

      {requireTypedConfirm && (
        <TypedConfirmInput
          value={typedValue}
          name={requireTypedConfirm}
          onChange={setTypedValue}
        />
      )}

      <div className={`flex justify-end gap-2 ${requireTypedConfirm ? 'mt-4' : 'mt-1'}`}>
        <button onClick={onClose} className="btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={
            variant === 'danger'
              ? 'bg-red-600 hover:bg-red-700 text-white rounded-[10px] border border-red-700 px-3 py-1.5 ggj-text-body font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
              : 'btn-primary'
          }
          disabled={isDisabled}
        >
          {loading ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
